/**
 * The weekly operations picture, as the person leading operations.
 *
 * It renders the `brief` lines from `/api/mo/ops/weekly` VERBATIM and in order. That is the
 * whole design. Those lines are written by `mo/flowOps.ts` against live Flow data, already
 * ranked hardest-first and already capped, and each one carries its own reading of what the
 * number means ("a cluster this tight is one cause, most likely how work is dated"). A client
 * that re-ordered, re-worded or truncated them would be substituting its judgement for the one
 * measured against the data.
 *
 * WHY NOTHING IS CHARTED HERE. Only 21 of 2,352 work packages carry an estimate, so a
 * utilisation bar would be invention, and the server refuses to report capacity in hours for
 * exactly that reason. The three headline counts are real, so they render as numbers; nothing
 * else does.
 *
 * WHY A FAILURE IS LOUD. An operations panel that renders nothing when Flow is unreachable
 * reads as "nothing outstanding" against a queue of a thousand open items. Unavailable says so,
 * in a sentence, and keeps the panel on the board.
 */

import { useCallback, useEffect, useState, type JSX } from 'react';
import { Icon } from './Icon.js';
import { fetchWeeklyOps, type WeeklyOpsResult } from './api.js';

function ageLabel(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '';
  const mins = Math.floor((Date.now() - t) / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

export function WeeklyOpsPanel(): JSX.Element | null {
  const [result, setResult] = useState<WeeklyOpsResult | null>(null);

  const load = useCallback(async () => {
    setResult(await fetchWeeklyOps());
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Still fetching. Nothing yet is not the same as nothing to say, so no panel until it answers.
  if (result === null) return null;

  if (result.state === 'unavailable') {
    return (
      <section className="deck-panel" data-hue="health">
        <header className="deck-panel-head">
          <Icon name="project" size={16} className="deck-panel-icon" />
          <h2 className="deck-panel-title">Weekly operations</h2>
        </header>
        <div className="deck-panel-body">
          <p className="deck-panel-note">Not known: {result.message}</p>
        </div>
      </section>
    );
  }

  const w = result.value;

  return (
    <section className="deck-panel" data-hue="health">
      <header className="deck-panel-head">
        <Icon name="project" size={16} className="deck-panel-icon" />
        <h2 className="deck-panel-title">Weekly operations</h2>
        <span className="deck-count">{w.headline.open}</span>
      </header>
      <div className="deck-panel-body">
        <p className="deck-panel-note">
          Open work across active projects{w.generatedAt !== '' ? `, read ${ageLabel(w.generatedAt)}` : ''}.
        </p>
        <div className="mo-ops-headline">
          <span className="mo-ops-figure">
            <strong>{w.headline.open}</strong> open
          </span>
          <span className="mo-ops-figure">
            <strong>{w.headline.overdue}</strong> overdue
          </span>
          <span className="mo-ops-figure">
            <strong>{w.headline.unassigned}</strong> unassigned
          </span>
        </div>
        <ul className="mo-ops-brief">
          {w.brief.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
        {/* What the figures deliberately do not measure, in the server's own words. */}
        {w.limitations.length > 0 && (
          <p className="deck-panel-note mo-ops-limits">Not measured: {w.limitations.join(' ')}</p>
        )}
      </div>
    </section>
  );
}
